// Heatmap dashboard — shows what the buyer did this session: time spent per
// room, where they looked (yaw/pitch heatmap with axis labels), which
// hotspots they clicked, and which variants they tried. Reads the local
// in-memory ring buffer of analytics events. Sprint 4+ will fetch
// aggregated cross-session data from PostHog's REST API.
//
// This is an operator tool, not a buyer one: it is reached through the menu's
// `dashboard` entry, which only exists under ?operator=1 (or ?edit=1 / ?embed=1).
// That entry also owns the `H` shortcut — hence no keydown listener here, since
// two listeners on one key would toggle twice and cancel out. `?dashboard=1`
// still auto-opens for a link someone was handed.

import { getAnalytics, isPostHogReady, type AnalyticsEvent } from '../playcanvas/analytics';

const YAW_BUCKET = 8;   // degrees per bucket → 45 cells across 360°
const PITCH_BUCKET = 8; // → ~22 cells across the full ±90°
const GAZE_INTERVAL_S = 1; // sampler ticks at 1 Hz; one sample = ~1 second viewed

interface SessionSummary {
  durationS: number;
  roomsVisited: number;
  hotspotsClicked: number;
  variantChanges: number;
  totalGazeSamples: number;
}

interface HotspotClickRecord {
  type: string;
  label: string;
  count: number;
  /** Per-source breakdown: how many clicks came from canvas tap vs VR controller vs gaze dwell. */
  sources: { click: number; controller: number; gaze: number };
  /** Hotspot's authored yaw/pitch — used to plot click markers on the gaze heatmap. */
  yaw?: number;
  pitch?: number;
}

interface ClickTimelineEntry {
  ts: number;
  /** Seconds since first event of the session. */
  sessionTimeS: number;
  roomId: string;
  hotspotLabel: string;
  hotspotType: string;
  source: string;
}

interface RoomActivity {
  roomId: string;
  gazeSamples: number;
  durationS: number;
  hotspotClicks: Map<string, HotspotClickRecord>;
  variants: Set<string>;
  /** [pitchBucket][yawBucket] → sample count */
  grid: number[][];
}

export class Dashboard {
  private readonly root: HTMLElement;
  private readonly summaryEl: HTMLElement;
  private readonly roomsEl: HTMLElement;
  private readonly hintEl: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;
  private isOpen = false;

  constructor() {
    this.root = mustGet('dashboard-overlay');
    this.summaryEl = mustGet('dashboard-summary');
    this.roomsEl = mustGet('dashboard-rooms');
    this.hintEl = mustGet('dashboard-hint');
    this.closeBtn = mustGet('dashboard-close') as HTMLButtonElement;
    this.closeBtn.addEventListener('click', () => this.close());
    this.renderHint();
    if (new URLSearchParams(window.location.search).get('dashboard') === '1') {
      queueMicrotask(() => this.open());
    }
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    this.isOpen = true;
    this.root.classList.add('is-open');
    this.render();
  }

  close(): void {
    this.isOpen = false;
    this.root.classList.remove('is-open');
  }

  // ── Internals ─────────────────────────────────────────────────

  private render(): void {
    const analytics = getAnalytics();
    const events = analytics.getAll();
    const summary = this.computeSummary(events);
    const activity = this.computeRoomActivity(events);
    const timeline = this.computeTimeline(events);

    this.renderSummary(summary);
    this.renderRooms(activity);
    this.renderTopHotspots(activity);
    this.renderTimeline(timeline);
    this.renderHint();
  }

  private renderHint(): void {
    if (isPostHogReady()) {
      this.hintEl.innerHTML =
        'Tecla <kbd>H</kbd> para fechar · ' +
        '<strong style="color:var(--gold)">PostHog ativo</strong> — ' +
        'cada visita desta URL aparece em ' +
        '<code>posthog.com → Live Events</code>. Veja agregações em ' +
        '<code>Insights → Trends/Funnels</code>.';
    } else {
      this.hintEl.innerHTML =
        'Tecla <kbd>H</kbd> para fechar · ' +
        'Para agregar várias sessões, configure ' +
        '<code>VITE_POSTHOG_KEY</code> antes de fazer build ' +
        '(ver <code>docs/ANALYTICS.md</code>).';
    }
  }

  // ── Aggregation ───────────────────────────────────────────────

  private computeSummary(events: readonly AnalyticsEvent[]): SessionSummary {
    let totalGazeSamples = 0;
    let hotspotsClicked = 0;
    let variantChanges = 0;
    const visitedRooms = new Set<string>();
    let firstTs = Infinity;
    let lastTs = 0;

    for (const e of events) {
      if (e.ts < firstTs) firstTs = e.ts;
      if (e.ts > lastTs) lastTs = e.ts;
      switch (e.name) {
        case 'gaze_sample': {
          totalGazeSamples += 1;
          const room = (e.payload as { room?: string } | undefined)?.room;
          if (room) visitedRooms.add(room);
          break;
        }
        case 'hotspot_clicked':
          hotspotsClicked += 1;
          break;
        case 'variant_changed':
          variantChanges += 1;
          break;
        case 'room_viewed': {
          const room = (e.payload as { room?: string } | undefined)?.room;
          if (room) visitedRooms.add(room);
          break;
        }
      }
    }

    const durationS = firstTs === Infinity ? 0 : Math.max(0, (lastTs - firstTs) / 1000);
    return {
      durationS,
      roomsVisited: visitedRooms.size,
      hotspotsClicked,
      variantChanges,
      totalGazeSamples,
    };
  }

  private computeRoomActivity(events: readonly AnalyticsEvent[]): RoomActivity[] {
    const byRoom = new Map<string, RoomActivity>();
    let currentRoom: string | null = null;

    const ensure = (roomId: string): RoomActivity => {
      let r = byRoom.get(roomId);
      if (!r) {
        r = {
          roomId,
          gazeSamples: 0,
          durationS: 0,
          hotspotClicks: new Map(),
          variants: new Set(),
          grid: Array.from({ length: 180 / PITCH_BUCKET }, () =>
            Array.from({ length: 360 / YAW_BUCKET }, () => 0),
          ),
        };
        byRoom.set(roomId, r);
      }
      return r;
    };

    for (const e of events) {
      const payload = e.payload as
        | { room?: string; yaw?: number; pitch?: number; variant?: string; hotspot?: string; label?: string; type?: string; source?: string; hotspotYaw?: number; hotspotPitch?: number }
        | undefined;

      switch (e.name) {
        case 'room_viewed': {
          if (payload?.room) currentRoom = payload.room;
          break;
        }
        case 'gaze_sample': {
          const roomId = payload?.room ?? currentRoom;
          if (!roomId) break;
          const r = ensure(roomId);
          r.gazeSamples += 1;
          r.durationS = r.gazeSamples * GAZE_INTERVAL_S;
          const yaw = payload?.yaw ?? 0;
          const pitch = payload?.pitch ?? 0;
          const yawBucket = Math.floor(((yaw % 360) + 360) % 360 / YAW_BUCKET);
          const pitchBucket = Math.floor((pitch + 90) / PITCH_BUCKET);
          const safeP = clampInt(pitchBucket, 0, r.grid.length - 1);
          const safeY = clampInt(yawBucket, 0, (r.grid[0]?.length ?? 1) - 1);
          const row = r.grid[safeP];
          if (row) row[safeY] = (row[safeY] ?? 0) + 1;
          break;
        }
        case 'hotspot_clicked': {
          const roomId = currentRoom;
          if (!roomId || !payload?.hotspot) break;
          const r = ensure(roomId);
          const id = payload.hotspot;
          const source = (payload.source ?? 'click') as 'click' | 'controller' | 'gaze';
          const entry = r.hotspotClicks.get(id);
          if (entry) {
            entry.count += 1;
            if (payload.label) entry.label = payload.label; // upgrade id → label once seen
            entry.sources[source] = (entry.sources[source] ?? 0) + 1;
            if (payload.hotspotYaw !== undefined) entry.yaw = payload.hotspotYaw;
            if (payload.hotspotPitch !== undefined) entry.pitch = payload.hotspotPitch;
          } else {
            r.hotspotClicks.set(id, {
              type: payload.type ?? 'unknown',
              label: payload.label ?? id,
              count: 1,
              sources: { click: 0, controller: 0, gaze: 0, [source]: 1 },
              yaw: payload.hotspotYaw,
              pitch: payload.hotspotPitch,
            });
          }
          break;
        }
        case 'variant_changed': {
          const roomId = currentRoom;
          if (!roomId) break;
          const r = ensure(roomId);
          if (payload?.variant) r.variants.add(payload.variant);
          break;
        }
      }
    }

    return Array.from(byRoom.values()).sort((a, b) => b.durationS - a.durationS);
  }

  private computeTimeline(events: readonly AnalyticsEvent[]): ClickTimelineEntry[] {
    const out: ClickTimelineEntry[] = [];
    let currentRoom: string | null = null;
    let firstTs = Infinity;
    for (const e of events) {
      if (e.ts < firstTs) firstTs = e.ts;
      const payload = e.payload as { room?: string; hotspot?: string; label?: string; type?: string; source?: string } | undefined;
      if (e.name === 'room_viewed' && payload?.room) {
        currentRoom = payload.room;
      } else if (e.name === 'hotspot_clicked' && currentRoom && payload?.hotspot) {
        out.push({
          ts: e.ts,
          sessionTimeS: (e.ts - firstTs) / 1000,
          roomId: currentRoom,
          hotspotLabel: payload.label ?? payload.hotspot,
          hotspotType: payload.type ?? 'unknown',
          source: payload.source ?? 'click',
        });
      }
    }
    return out;
  }

  // ── Rendering ─────────────────────────────────────────────────

  private renderSummary(s: SessionSummary): void {
    this.summaryEl.innerHTML = '';
    const stats: Array<[string, string]> = [
      ['Tempo de sessão', formatDuration(s.durationS)],
      ['Ambientes visitados', String(s.roomsVisited)],
      ['Hotspots clicados', String(s.hotspotsClicked)],
      ['Variações testadas', String(s.variantChanges)],
      ['Amostras de olhar', String(s.totalGazeSamples)],
    ];
    for (const [label, value] of stats) {
      const tile = document.createElement('div');
      tile.className = 'dash-stat';
      const v = document.createElement('div');
      v.className = 'dash-stat__value';
      v.textContent = value;
      const l = document.createElement('div');
      l.className = 'dash-stat__label';
      l.textContent = label;
      tile.append(v, l);
      this.summaryEl.appendChild(tile);
    }
  }

  private renderRooms(rooms: RoomActivity[]): void {
    this.roomsEl.innerHTML = '';
    if (rooms.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'dash-empty';
      empty.textContent = 'Sem dados ainda — explore os ambientes por alguns segundos e reabra (H).';
      this.roomsEl.appendChild(empty);
      return;
    }

    for (const room of rooms) {
      const card = document.createElement('article');
      card.className = 'dash-room';

      // Header — room name + headline metrics
      const header = document.createElement('header');
      header.className = 'dash-room__header';
      const title = document.createElement('h3');
      title.textContent = room.roomId;
      const meta = document.createElement('div');
      meta.className = 'dash-room__meta';
      meta.innerHTML = `
        <span><strong>${formatDuration(room.durationS)}</strong> de visualização</span>
        <span>${room.hotspotClicks.size} hotspot${room.hotspotClicks.size === 1 ? '' : 's'} clicado${room.hotspotClicks.size === 1 ? '' : 's'}</span>
        <span>${room.variants.size} variação${room.variants.size === 1 ? '' : 'ões'}</span>
      `;
      header.append(title, meta);
      card.appendChild(header);

      // Heatmap canvas
      const canvas = document.createElement('canvas');
      canvas.className = 'dash-room__heatmap';
      card.appendChild(canvas);

      // Hotspot click list — show ALL hotspots ranked by clicks, with source breakdown
      // (canvas tap vs VR controller vs gaze) when more than one source contributed.
      if (room.hotspotClicks.size > 0) {
        const list = document.createElement('ul');
        list.className = 'dash-room__hotspots';
        const sorted = Array.from(room.hotspotClicks.values()).sort((a, b) => b.count - a.count);
        for (const hit of sorted) {
          const li = document.createElement('li');
          const sourceParts: string[] = [];
          if (hit.sources.click) sourceParts.push(`${hit.sources.click}↶`); // tap / mouse
          if (hit.sources.controller) sourceParts.push(`${hit.sources.controller}🎮`); // VR controller
          if (hit.sources.gaze) sourceParts.push(`${hit.sources.gaze}👁️`); // gaze dwell
          const sourceTitle =
            `Cliques de tela: ${hit.sources.click}` +
            ` · Controle VR: ${hit.sources.controller}` +
            ` · Gaze (olhar): ${hit.sources.gaze}`;
          li.innerHTML = `
            <span class="dash-room__hotspot-type" data-type="${hit.type}" title="${hit.type}"></span>
            <span class="dash-room__hotspot-label">${escapeHtml(hit.label)}</span>
            <span class="dash-room__hotspot-count">×${hit.count}</span>
            <span class="dash-room__hotspot-sources" title="${sourceTitle}">${sourceParts.join(' ')}</span>
          `;
          list.appendChild(li);
        }
        card.appendChild(list);
      }

      this.roomsEl.appendChild(card);

      // Draw the heatmap after the canvas is in the DOM (so clientWidth resolves).
      queueMicrotask(() => this.drawHeatmap(canvas, room.grid, room.hotspotClicks));
    }
  }

  /**
   * Cross-room "Top hotspots clicados" bar chart. Aggregates every room's
   * hotspot clicks into a single ranking with horizontal bars sized by
   * count. Rep can see at a glance which 3-5 hotspots got the most
   * attention across the whole session — useful for prioritizing the
   * follow-up call ("você abriu a marcenaria 4 vezes, vamos falar dela").
   */
  private renderTopHotspots(rooms: RoomActivity[]): void {
    const existing = this.roomsEl.querySelector('.dash-top-hotspots');
    if (existing) existing.remove();
    const all: Array<{ roomId: string; label: string; count: number; type: string }> = [];
    for (const room of rooms) {
      for (const hit of room.hotspotClicks.values()) {
        all.push({ roomId: room.roomId, label: hit.label, count: hit.count, type: hit.type });
      }
    }
    if (all.length === 0) return;
    all.sort((a, b) => b.count - a.count);
    const top = all.slice(0, 8);
    const max = top[0]?.count ?? 1;

    const wrap = document.createElement('section');
    wrap.className = 'dash-top-hotspots';
    const h = document.createElement('h3');
    h.textContent = 'Hotspots mais clicados';
    wrap.appendChild(h);
    const list = document.createElement('ol');
    list.className = 'dash-top-hotspots__list';
    for (const item of top) {
      const pct = (item.count / max) * 100;
      const li = document.createElement('li');
      li.innerHTML = `
        <span class="dash-room__hotspot-type" data-type="${item.type}" title="${item.type}"></span>
        <span class="dash-top-hotspots__label">${escapeHtml(item.label)}</span>
        <span class="dash-top-hotspots__room">${escapeHtml(item.roomId)}</span>
        <span class="dash-top-hotspots__bar"><span class="dash-top-hotspots__bar-fill" style="width:${pct.toFixed(1)}%"></span></span>
        <span class="dash-top-hotspots__count">×${item.count}</span>
      `;
      list.appendChild(li);
    }
    wrap.appendChild(list);
    // Prepend so it appears above the per-room cards.
    this.roomsEl.insertBefore(wrap, this.roomsEl.firstChild);
  }

  private renderTimeline(entries: ClickTimelineEntry[]): void {
    // Inject below the rooms list. If the timeline section already exists from
    // a prior render, replace it.
    const existing = this.roomsEl.querySelector('.dash-timeline');
    if (existing) existing.remove();
    if (entries.length === 0) return;

    const wrap = document.createElement('section');
    wrap.className = 'dash-timeline';
    const heading = document.createElement('h3');
    heading.textContent = `Sequência de cliques (${entries.length})`;
    wrap.appendChild(heading);

    const ol = document.createElement('ol');
    ol.className = 'dash-timeline__list';
    for (const entry of entries) {
      const li = document.createElement('li');
      li.innerHTML = `
        <span class="dash-timeline__time">${formatDuration(entry.sessionTimeS)}</span>
        <span class="dash-room__hotspot-type" data-type="${entry.hotspotType}"></span>
        <span class="dash-timeline__label"><strong>${escapeHtml(entry.hotspotLabel)}</strong> em ${escapeHtml(entry.roomId)}</span>
        <span class="dash-timeline__source" title="${entry.source}">${sourceIcon(entry.source)}</span>
      `;
      ol.appendChild(li);
    }
    wrap.appendChild(ol);
    this.roomsEl.appendChild(wrap);
  }

  private drawHeatmap(canvas: HTMLCanvasElement, grid: number[][], clicks: Map<string, HotspotClickRecord> = new Map()): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;
    canvas.width = cssW * dpr;
    canvas.height = cssH * dpr;
    ctx.scale(dpr, dpr);

    // Background
    ctx.fillStyle = '#0a0a0e';
    ctx.fillRect(0, 0, cssW, cssH);

    // Plot area = canvas minus axis margins
    const padL = 38;
    const padR = 8;
    const padT = 8;
    const padB = 22;
    const plotW = cssW - padL - padR;
    const plotH = cssH - padT - padB;

    // Heatmap cells
    let max = 0;
    for (const row of grid) for (const v of row) if (v > max) max = v;
    max = Math.max(1, max);
    const cols = grid[0]?.length ?? 1;
    const rows = grid.length;
    const cellW = plotW / cols;
    const cellH = plotH / rows;

    for (let py = 0; py < rows; py += 1) {
      const row = grid[py];
      if (!row) continue;
      for (let px = 0; px < cols; px += 1) {
        const v = row[px] ?? 0;
        if (v === 0) continue;
        const t = v / max;
        const r = Math.round(20 + 220 * Math.min(1, t * 1.1));
        const g = Math.round(0 + 200 * Math.max(0, t - 0.2));
        const b = Math.round(80 - 60 * t);
        const a = 0.18 + 0.82 * t;
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a})`;
        // Flip pitch so up-axis = up-screen.
        const yy = padT + (rows - 1 - py) * cellH;
        ctx.fillRect(padL + px * cellW, yy, Math.ceil(cellW), Math.ceil(cellH));
      }
    }

    // Reference grid lines + labels
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
    ctx.lineWidth = 1;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = '10px ui-monospace, monospace';
    ctx.textBaseline = 'middle';

    // Yaw ticks at 0, 90, 180, 270
    const yawTicks: Array<[number, string]> = [[0, '0°'], [90, '90°'], [180, '180°'], [270, '270°']];
    ctx.textAlign = 'center';
    for (const [yawDeg, label] of yawTicks) {
      const x = padL + (yawDeg / 360) * plotW;
      ctx.beginPath();
      ctx.moveTo(x, padT);
      ctx.lineTo(x, padT + plotH);
      ctx.stroke();
      ctx.fillText(label, x, cssH - 8);
    }

    // Pitch reference (horizon at 0°)
    ctx.textAlign = 'right';
    const pitchTicks: Array<[number, string]> = [[60, 'cima'], [0, 'horizonte'], [-60, 'baixo']];
    for (const [pitchDeg, label] of pitchTicks) {
      const yy = padT + plotH * (1 - (pitchDeg + 90) / 180);
      if (pitchDeg === 0) {
        ctx.strokeStyle = 'rgba(244, 196, 48, 0.45)';
        ctx.beginPath();
        ctx.moveTo(padL, yy);
        ctx.lineTo(padL + plotW, yy);
        ctx.stroke();
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.18)';
      }
      ctx.fillText(label, padL - 6, yy);
    }

    // Plot frame
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.strokeRect(padL, padT, plotW, plotH);

    // Yaw axis label
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.fillText('direção do olhar (yaw)', padL + plotW / 2, cssH - 22);

    // Click markers — gold circles at each hotspot's (yaw, pitch), radius
    // scaled by click count. The rep can see "buyer looked AND clicked"
    // versus "looked but didn't click" by comparing markers to heat.
    if (clicks.size > 0) {
      let maxC = 0;
      for (const hit of clicks.values()) if (hit.count > maxC) maxC = hit.count;
      maxC = Math.max(1, maxC);
      ctx.lineWidth = 1.5;
      for (const hit of clicks.values()) {
        if (hit.yaw === undefined || hit.pitch === undefined) continue;
        const yawNorm = ((hit.yaw % 360) + 360) % 360;
        const px = padL + (yawNorm / 360) * plotW;
        const py = padT + plotH * (1 - (hit.pitch + 90) / 180);
        const radius = 4 + 6 * (hit.count / maxC);
        ctx.fillStyle = 'rgba(244, 196, 48, 0.85)';
        ctx.strokeStyle = 'rgba(20, 18, 12, 0.9)';
        ctx.beginPath();
        ctx.arc(px, py, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }
  }
}

function clampInt(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function sourceIcon(source: string): string {
  switch (source) {
    case 'click':      return '↶';
    case 'controller': return '🎮';
    case 'gaze':       return '👁️';
    default:           return '·';
  }
}

function formatDuration(s: number): string {
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const sec = Math.round(s - m * 60);
  return `${m}m${sec.toString().padStart(2, '0')}s`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function mustGet(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`[dashboard] missing #${id}`);
  return el;
}
